import React from "react";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";

type TLoadingState = {
  status: string;
  message?: React.ReactNode;
};

type TLoadingSuccessErrorAlertProps = {
  loading: TLoadingState;
  /**
   * Dead since the Retry button was commented out — kept so no call site
   * breaks; nothing renders that could invoke it (pinned).
   */
  handle_reload?: (() => void) | null;
  hide_error?: boolean;
};

/**
 * The Rules-owned status strip on DS Alert + Spinner (§ 99) — the same
 * migration its Products twin received in § 74, with one fix the twin didn't
 * need: legacy painted SUCCESS as bootstrap `alert-primary` (blue), so
 * "uploaded successfully" wore the same ink as "uploading". Success is the
 * success tone now. The Spinner replaces the fa-circle-notch spin like for
 * like (aria-hidden — the message text carries the announcement; the Alert is
 * the live region). Consumed by the Guide UploadModal (§ 98), Product Export,
 * PDW download and the three PDW upload screens.
 *
 * Contract preserved exactly: `Saving`/`Loading` always render; `Error`
 * renders unless `hide_error`; `Success` renders only while
 * `message !== undefined` (null and '' both render — shipped quirk, pinned);
 * anything else renders nothing.
 */
const LoadingSuccessErrorAlert = ({ loading, hide_error }: TLoadingSuccessErrorAlertProps) => {
  if (loading.status === "Saving" || loading.status === "Loading") {
    return (
      /* § 328 — `icon={null}`: the spinner IS this banner's leading mark. The info
         tone's "i" beside it read as two icons (PDW upload dialogs, 2026-09-25). The
         tone, the live announcement and the message are unchanged. */
      <Alert tone="info" live icon={null} data-testid="loading-status-alert">
        <span className="tw-inline-flex tw-items-center tw-gap-2">
          <Spinner size="sm" aria-hidden="true" data-testid="loading-status-spinner" />
          <span>{loading.message}</span>
        </span>
      </Alert>
    );
  }
  if (loading.status === "Error" && !hide_error) {
    return (
      <Alert tone="danger" live data-testid="loading-status-alert">
        {loading.message}
      </Alert>
    );
  }
  if (loading.status === "Success" && loading.message !== undefined) {
    return (
      <Alert tone="success" live data-testid="loading-status-alert">
        {loading.message}
      </Alert>
    );
  }
  return null;
};
export default LoadingSuccessErrorAlert;
